import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as LocalAuthentication from 'expo-local-authentication';
import apis from '@api/api';
import {
	SWITCH_COMPANY_REQUEST,
	HIDE_LOADING_MODAL,
	SHOW_LOADING_MODAL,
	HIDE_ALL_MODALS,
	CLEAR_COMPANY,
	INITIALISE_MENU,
	RESET_APP,
	TOGGLE_EASY_MODE,
	START_ONBOARDING,
} from '@reduxLocal/actions/types';
import { store } from '@reduxLocal/persistState';

export interface SessionData {
	isAuthenticated: boolean;
	company?: {
		_id: string;
		logo: string;
		copmany_name: string;
	};
	[key: string]: any;
}

export interface AuthOptions {
	dispatch: any;
	navigation: any;
	loggedOut: boolean;
	userId?: string;
}

/**
 * Check if biometric/passkey authentication is available
 */
export const checkAuthAvailability = async () => {
	// Skip biometric auth check on web
	if (Platform.OS === 'web') {
		return {
			available: false,
			hasHardware: false,
			isEnrolled: false,
			supportedTypes: [],
			hasFaceID: false,
			hasFingerprintID: false,
			hasIris: false,
		};
	}

	try {
		const hasHardware = await LocalAuthentication.hasHardwareAsync();
		const isEnrolled = await LocalAuthentication.isEnrolledAsync();
		const supportedTypes =
			await LocalAuthentication.supportedAuthenticationTypesAsync();

		return {
			available: hasHardware && isEnrolled,
			hasHardware,
			isEnrolled,
			supportedTypes,
			hasFaceID: supportedTypes.includes(
				LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION
			),
			hasFingerprintID: supportedTypes.includes(
				LocalAuthentication.AuthenticationType.FINGERPRINT
			),
			hasIris: supportedTypes.includes(
				LocalAuthentication.AuthenticationType.IRIS
			),
		};
	} catch (error) {
		console.warn('Error checking auth availability:', error);
		return {
			available: false,
			hasHardware: false,
			isEnrolled: false,
			supportedTypes: [],
			hasFaceID: false,
			hasFingerprintID: false,
			hasIris: false,
		};
	}
};;

/**
 * Perform biometric/passkey authentication
 */
export const performBiometricAuth = async (options?: {
	promptMessage?: string;
	cancelLabel?: string;
	fallbackLabel?: string;
	disableDeviceFallback?: boolean;
}): Promise<{ success: boolean; error?: string }> => {
	try {
		const authAvailability = await checkAuthAvailability();

		if (!authAvailability.available) {
			console.log('Biometric auth not available, skipping');
			return { success: true }; // Don't block if not available
		}

		// Determine auth prompt message based on available type
		let promptMessage =
			options?.promptMessage || 'Authenticate to continue';
		if (authAvailability.hasFaceID) {
			promptMessage = 'Use Face ID to continue';
		} else if (authAvailability.hasFingerprintID) {
			promptMessage = 'Use your fingerprint to continue';
		} else if (Platform.OS === 'web') {
			promptMessage = 'Use your passkey to continue';
		}

		const authResult = await LocalAuthentication.authenticateAsync({
			promptMessage,
			cancelLabel: options?.cancelLabel || 'Cancel',
			fallbackLabel: options?.fallbackLabel || 'Use Password',
			disableDeviceFallback: options?.disableDeviceFallback || false,
		});

		if (authResult.success) {
			console.log('✅ Biometric authentication successful');
			return { success: true };
		} else if (authResult.error === 'user_cancel') {
			console.log('⚠️ User cancelled biometric auth');
			return { success: false, error: 'user_cancel' }; // Block on user cancel
		} else {
			console.warn('❌ Biometric auth failed:', authResult.error);
			return { success: false, error: authResult.error };
		}
	} catch (error) {
		console.warn('Biometric auth exception:', error);
		return { success: false, error: 'biometric_exception' }; // Block on errors
	}
};

/**
 * Fetch and validate session, with optional biometric auth
 */
export const fetchAndValidateSession = async ({
	dispatch,
	navigation,
	loggedOut,
	userId,
}: AuthOptions): Promise<{ success: boolean; error?: string }> => {
	console.log('[fetchAndValidateSession] Called', { userId, loggedOut });
	
	if (!userId || loggedOut) {
		console.log('[fetchAndValidateSession] No user ID or user logged out, skipping session fetch');
		return { success: false, error: 'no_user_id' };
	}

	try {
		// Check TC modal state before proceeding
		const tcModalVisible = store.getState()?.modals?.tcModal?.modalDisplayed;
		console.log('[fetchAndValidateSession] TC modal state:', tcModalVisible);
		
		if (tcModalVisible) {
			console.log('[fetchAndValidateSession] TC modal visible, aborting session fetch');
			return { success: false, error: 'tc_modal_visible' };
		}

		dispatch({ type: SHOW_LOADING_MODAL });
		dispatch({ type: HIDE_ALL_MODALS });

		const session = await apis.getSession();

		if (!session?.data) {
			throw new Error('Invalid session response');
		}

		// Update company or target user if present
		// PRIORITY: companyNumber > targetUserId > personal account
		if (session.data.company) {
			const item = session.data.company;
			dispatch({
				type: SWITCH_COMPANY_REQUEST,
				payload: {
					id: item._id,
					logo: item.logo,
					companyName: item.company_name || item.copmany_name,
				},
			});
		} else if (session.data.targetUser) {
			// Target user context - clear company selection (target user is personal account mode)
			dispatch({ type: CLEAR_COMPANY });
		} else {
			// Personal account - ensure company is cleared
			dispatch({ type: CLEAR_COMPANY });
		}

		const { isAuthenticated } = session.data;

		if (isAuthenticated) {
			// Check if biometric auth is available before attempting it
			const authAvailability = await checkAuthAvailability();

			// Skip biometric auth on web or if not available
			const shouldSkipBiometric =
				Platform.OS === 'web' || !authAvailability.available;

			let authResult = { success: true };

			if (!shouldSkipBiometric) {
				// Perform biometric auth if available (mobile only)
				authResult = await performBiometricAuth({
					promptMessage: 'Welcome back! Authenticate to continue',
				});
			} else {
				console.log('Skipping biometric auth (web or not available)');
			}

			if (authResult.success) {
				// Save session to storage
				await AsyncStorage.setItem(
					'userData',
					JSON.stringify(session.data)
				);

				// Initialize menu and navigate
				if (Platform.OS === 'web') {
					dispatch({ type: INITIALISE_MENU });
				}

				// Check if user is new (hasn't completed tutorial)
				// Also check if user has any existing data indicating they've used the app beyond registration
				// This prevents navigation to EasyMode if user skipped onboarding but backend hasn't updated yet
				const tutorialModal = session.data?.userData?.tutorials?.tutorialModal;
				const userData = session.data?.userData;
				
				// Check for indicators that user has actually used the app (not just registered)
				// These fields are typically set after registration/initial setup
				const hasCompany = session.data?.company || userData?.currentCompany;
				const hasIntegrations = userData?.integrations && Object.keys(userData.integrations).length > 0;
				const hasLoginHistory = userData?.login_history?.previous_logins?.length > 0;
				const hasStickyNotes = userData?.sticky_notes?.length > 0;
				const hasUserInitialised = userData?.user_initialised === true;
				
				// User has activity if they have any of these indicators
				const hasAnyUserActivity = hasCompany || hasIntegrations || hasLoginHistory || hasStickyNotes || hasUserInitialised;
				
				// If tutorialModal is explicitly true, user has completed or skipped onboarding - never redirect to EasyMode
				// User is only considered "new" if tutorialModal is explicitly false AND they have no existing activity
				// If tutorialModal is undefined/null, treat conservatively - assume user has skipped onboarding unless
				// we have strong evidence they're truly new (no activity AND this appears to be first login)
				const isNewUser = tutorialModal === false && !hasAnyUserActivity;

				// Check if TC modal is visible before navigating - don't navigate if user needs to accept terms
				// But also check if terms were just accepted (accepted flag is true)
				const tcModalState = store.getState()?.modals?.tcModal;
				const tcModalVisibleBeforeNav = tcModalState?.modalDisplayed;
				const termsAccepted = tcModalState?.accepted === true;
				console.log('[fetchAndValidateSession] Checking TC modal before navigation:', { 
					visible: tcModalVisibleBeforeNav, 
					accepted: termsAccepted 
				});
				
				// Only skip navigation if modal is visible AND terms haven't been accepted
				if (tcModalVisibleBeforeNav && !termsAccepted) {
					console.log('[fetchAndValidateSession] TC modal is visible and terms not accepted, skipping navigation');
					dispatch({ type: HIDE_LOADING_MODAL });
					return { success: false, error: 'tc_modal_visible' };
				}

				if (isNewUser) {
					console.log("[fetchAndValidateSession] [new user], starting onboarding on native");
					// Enable EasyMode and start onboarding for new users
					dispatch({ type: TOGGLE_EASY_MODE });
					// Delay tutorial modal to give users time to see the dashboard and for elements to mount
					setTimeout(() => dispatch({ type: START_ONBOARDING }), 3500);
					// Navigate to EasyMode screen
					console.log('[fetchAndValidateSession] Navigating to Dashboard/EasyMode');
					navigation.replace('Dashboard', {
						screen: 'EasyMode',
						params: { session: session.data },
					});
				} else {
					console.log('[fetchAndValidateSession] Navigating to Dashboard/Welcome');
					navigation.replace('Dashboard', {
						screen: 'Welcome',
						params: { session: session.data },
					});
				}

				dispatch({ type: HIDE_LOADING_MODAL });
				return { success: true };
			} else {
				// Biometric failed - don't block, just show error
				console.warn(
					'Biometric authentication failed, please log in manually'
				);
				dispatch({ type: HIDE_LOADING_MODAL });
				return { success: false, error: 'auth_failed' };
			}
		} else {
			// Not authenticated
			dispatch({ type: RESET_APP });
			dispatch({ type: HIDE_LOADING_MODAL });
			return { success: false, error: 'not_authenticated' };
		}
	} catch (error) {
		console.error('Session fetch error:', error);
		dispatch({ type: HIDE_LOADING_MODAL });

		// Try to recover from local storage
		try {
			const storedUser = await AsyncStorage.getItem('userData');

			if (storedUser) {
				const data = JSON.parse(storedUser);

				// Check if session is expired
				if (data?.expireAt && new Date(data.expireAt) > new Date()) {
					// Check if biometric auth is available before attempting it
					const authAvailability = await checkAuthAvailability();
					const shouldSkipBiometric =
						Platform.OS === 'web' || !authAvailability.available;

					let authResult = { success: true };

					if (!shouldSkipBiometric) {
						// Session still valid - try biometric auth (mobile only)
						authResult = await performBiometricAuth();
					} else {
						console.log(
							'Skipping biometric auth in recovery (web or not available)'
						);
					}

					if (authResult.success) {
						// Check if user is new (hasn't completed tutorial)
						// Also check if user has any existing data indicating they've used the app beyond registration
						// This prevents navigation to EasyMode if user skipped onboarding but backend hasn't updated yet
						const tutorialModal = data?.userData?.tutorials?.tutorialModal;
						const userData = data?.userData;
						
						// Check for indicators that user has actually used the app (not just registered)
						// These fields are typically set after registration/initial setup
						const hasCompany = data?.company || userData?.currentCompany;
						const hasIntegrations = userData?.integrations && Object.keys(userData.integrations).length > 0;
						const hasLoginHistory = userData?.login_history?.previous_logins?.length > 0;
						const hasStickyNotes = userData?.sticky_notes?.length > 0;
						const hasUserInitialised = userData?.user_initialised === true;
						
						// User has activity if they have any of these indicators
						const hasAnyUserActivity = hasCompany || hasIntegrations || hasLoginHistory || hasStickyNotes || hasUserInitialised;

						console.log('hasAnyUserActivity', hasAnyUserActivity);
						
						// If tutorialModal is explicitly true, user has completed or skipped onboarding - never redirect to EasyMode
						// User is only considered "new" if tutorialModal is explicitly false AND they have no existing activity
						// If tutorialModal is undefined/null, treat conservatively - assume user has skipped onboarding unless
						// we have strong evidence they're truly new (no activity AND this appears to be first login)
						const isNewUser = tutorialModal === false && !hasAnyUserActivity;

						if (isNewUser) {
							console.log("[new user], starting onboarding on native platfor");
							// Enable EasyMode and start onboarding for new users
							dispatch({ type: TOGGLE_EASY_MODE });
							// Delay tutorial modal to give users time to see the dashboard and for elements to mount
							setTimeout(() => dispatch({ type: START_ONBOARDING }), 3500);
							// Navigate to EasyMode screen
							navigation.replace('Dashboard', {
								screen: 'EasyMode',
								params: { session: data },
							});
						} else {
							navigation.replace('Dashboard', {
								screen: 'Welcome',
								params: { session: data },
							});
						}
						return { success: true };
					}
				} else {
					// Session expired, clear it
					await AsyncStorage.removeItem('userData');
				}
			}
		} catch (storageError) {
			console.error('Local storage recovery failed:', storageError);
		}

		return { success: false, error: error.message };
	}
};

/**
 * Clear local session storage
 */
export const clearLocalSession = async (): Promise<void> => {
	try {
		await AsyncStorage.removeItem('userData');
		console.log('Local session cleared');
	} catch (error) {
		console.error('Failed to clear local session:', error);
	}
};

/**
 * Save session to local storage with expiration
 */
export const saveSessionToLocal = async (
	sessionData: any,
	expirationDays: number = 7
): Promise<void> => {
	try {
		const dataToStore = {
			...sessionData,
			expireAt: Date.now() + 1000 * 60 * 60 * 24 * expirationDays,
		};
		await AsyncStorage.setItem('userData', JSON.stringify(dataToStore));
		console.log('Session saved to local storage');
	} catch (error) {
		console.error('Failed to save session:', error);
	}
};

/**
 * Get authentication method description for UI display
 */
export const getAuthMethodDescription = async (): Promise<string> => {
	const authInfo = await checkAuthAvailability();

	if (!authInfo.available) {
		return 'Password';
	}

	if (Platform.OS === 'web') {
		return 'Passkey';
	}

	if (authInfo.hasFaceID) {
		return 'Face ID';
	}

	if (authInfo.hasFingerprintID) {
		return 'Fingerprint';
	}

	if (authInfo.hasIris) {
		return 'Iris Scan';
	}

	return 'Biometric';
};
